import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { partnerApi } from "../../../../../../../../../../services/Api/Partner/partnerApi";
import { api } from "../../../../../../../../../../services/Api/api";
import { OrganizationApi } from "../../../../../../../../../../services/Api/types";
import { selectPartner } from "../../../../../../../../../../services/partner/selectors";
import OrganizationPreview from "../../../../../../../../components/Admin/Organization/OrganizationPreview";
import { OrganizationPreviewRequests } from "../../../../../../../../components/Admin/Organization/services/types";

interface MatchParams {
	organizationId: string;
}

const PartnerPreviewOrganizationsPreviewPage = (props: RouteComponentProps) => {
	const partner = useSelector(selectPartner);
	const [organization, setOrganization] = useState<OrganizationApi | undefined>();
	const { addFlash } = useFlash();
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>();
	const { organizationId } = useParams<MatchParams>();
	const partnerId = Number(partner?.id);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation("admin");

	useEffect(() => {
		handleInit();
	}, []);

	const handleInit = async () => {
		await fetchResourceConfig();
		await fetchOrganization();
	};

	const organizationPreviewRequests = {
		get: async (id: number) => {
			const params = { include: "place,applications,custom_fields,terminals,stats" };
			return partnerApi.getPartnerOrganization(partnerId, id, params);
		},
		users: async (id: number) => partnerApi.getOrganizationUsers(partnerId, id),
		partners: async (id: number) => partnerApi.getOrganizationPartners(partnerId, id),
		subscriptions: async (id: number) => partnerApi.getOrganizationSubscriptions(partnerId, id),
		subscriptionChangeTrial: async (id: number, data: { trial: boolean }) =>
			partnerApi.changeSubscriptionTrial(partnerId, id, data),
		terminals: async (id: number) => partnerApi.getTerminals(partnerId, id),
		invoices: async (id: number) => partnerApi.getOrganizationInvoices(partnerId, id),
		resendInvitation: async (organizationId: number, invitationId: number) =>
			partnerApi.resendInvitation(partnerId, organizationId, invitationId),
		extendPaymentDate: async (id: number) => partnerApi.extendSubscriptionPayment(partnerId, id),
		exportProformaInvoicePdf: async (organizationId: number, proformaId: string) =>
			partnerApi.getSubscriptionProformaPdf(partnerId, organizationId, proformaId),
		getProducts: async (id: number, params?: Record<string, any>, options?: Record<string, any>) =>
			partnerApi.getOrganizationProductsPerCategory(partnerId, id, params, options),
		updateFeatures: (organizationId: number, data: any) =>
			partnerApi.updateOrganizationFeatures(partnerId, organizationId, data),
		getFeatures: (organizationId: number) => partnerApi.getOrganizationFeatures(partnerId, organizationId),
	} as OrganizationPreviewRequests;

	const fetchOrganization = async () => {
		try {
			const response = await organizationPreviewRequests.get(Number(organizationId));
			setOrganization(response);
			handleChangeTabTitle(
				`${response.name} | ${t("lib:common.word.locations")} | ${t(
					"modules.partner.field.partner_panel.title"
				)}`
			);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchResourceConfig = async () => {
		try {
			const params = {
				resource_name: "PARTNER_ORGANIZATION",
				custom_field_resource_type: "ORGANIZATION",
				custom_field_permission_scope: "WRITE",
			};
			const res = await api.getResourceConfig(params);
			setCustomFieldsConfig(res.custom_fields);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!organization || !customFieldsConfig) {
		return <LoadingContainer />;
	}

	return (
		<OrganizationPreview
			organization={organization}
			url={props.match.url}
			refresh={fetchOrganization}
			context={"PARTNER"}
			requests={organizationPreviewRequests}
			customFieldsConfig={customFieldsConfig}
		/>
	);
};
export default PartnerPreviewOrganizationsPreviewPage;
